/**
 *
 * InvoiceDetailPage
 *
 */

import React, { useEffect } from 'react';
import Magnifier from 'react-magnifier';
import { useInView } from 'react-intersection-observer';

// api
import { InvoicePage } from 'lib/api/invoice';
import { OCRPage } from 'lib/api/ocrResult';
import { generateImageURL } from 'lib/api/images';

// modules
import { OCRTextPage, OCRTextPageDetection, OCRTextPageDetections } from 'modules/OCRTextPage';

// decorate
import { decorate, Decorate } from './decorate';

export interface InvoiceDetailPageProps {
  id?: string;
  lazyLoad: boolean;
  thresholds: {};
  zoomWidth: string;
  onScaleChange: (s: number) => void;
  returnHeight: (id: string | undefined, height: number) => void;
  containerId: UUID;
  invoiceId: UUID;
  invoicePage: InvoicePage;
  ocrTextPageDetections: OCRTextPageDetections;
  highlightedDetection: OCRTextPageDetection | null;
  onDetectionClick: (detection: OCRTextPageDetection) => void;
  outlined: boolean;
  magnifier: boolean;
  ocrPageInfo: OCRPage;
}

const InvoiceDetailPage: React.FC<InvoiceDetailPageProps & Decorate> = (props) => {
  const {
    classes,
    onScaleChange,
    id,
    thresholds,
    zoomWidth,
    returnHeight,
    invoicePage,
    ocrTextPageDetections,
    outlined,
    magnifier,
    highlightedDetection,
    onDetectionClick,
    lazyLoad,
    ocrPageInfo,
  } = props;

  React.useEffect(() => {
    const lastInvoice = document.getElementById(id || '2');
    if (lastInvoice) returnHeight(id, lastInvoice.offsetTop + lastInvoice.offsetHeight);
  });

  //background:#F1F1FA for preview box before invoice image is loaded
  const { ref, inView, entry } = useInView({
    threshold: 0.2,
  });

  useEffect(() => {
    if (inView && entry && lazyLoad) {
      entry.target.setAttribute('src', generateImageURL(invoicePage.Src));
    }
  }, [inView]);

  useEffect(() => {
    if (!lazyLoad && entry) {
      entry.target.setAttribute('src', generateImageURL(invoicePage.Src));
    }
  }, [entry]);

  return (
    <div id={id} style={{ width: zoomWidth + 'px' }} className={classes.page}>
      {magnifier ? (
        <Magnifier
          className={classes.magnifier}
          src={generateImageURL(invoicePage.Src)}
          mgWidth={200}
          mgHeight={200}
        />
      ) : (
        <>
          <img
            ref={ref}
            width={zoomWidth}
            height={Number(zoomWidth) / (ocrPageInfo.Width / ocrPageInfo.Height)}
            className={classes.image}
          />
          <OCRTextPage
            thresholds={thresholds}
            onScaleChange={onScaleChange}
            className={classes.ocrText}
            page={ocrPageInfo}
            detections={ocrTextPageDetections}
            highlightedDetection={highlightedDetection}
            onDetectionClick={onDetectionClick}
            outlinedPage={outlined}
            outlinedRegions={outlined}
            outlinedLines={outlined}
            outlinedWords={outlined}
          />
        </>
      )}
    </div>
  );
};

const StyledInvoiceDetailPage = decorate(InvoiceDetailPage);
export { StyledInvoiceDetailPage as InvoiceDetailPage };
